<template>
  <div class="max-w-[664px]">
    <div v-if="!passkeySupported" class="text-sm text-gpx_gray-700">
      Your browser does not support passkeys.
    </div>
    <template v-else>
      <div v-if="isLoadingCredentials" class="text-sm text-gpx_gray-700">
        Loading passkeys...
      </div>
      <div v-else>
        <div v-if="credentials?.length" class="mb-4 space-y-3">
          <div
            v-for="cred in credentials"
            :key="cred.id"
            class="flex items-center justify-between rounded-md border border-gpx_gray-900 px-4 py-3"
          >
            <div>
              <p class="text-sm font-medium text-gpx_slate-100">{{ cred.name || 'Passkey' }}</p>
              <p class="text-xs text-gpx_gray-700">
                Added {{ formatDate(cred.created_at) }}
                <span v-if="cred.last_used_at"> &middot; Last used {{ formatDate(cred.last_used_at) }}</span>
              </p>
            </div>
            <button
              type="button"
              class="text-sm text-gpx_red-200 hover:text-gpx_red-100"
              :disabled="deletingId === cred.id"
              @click="handleDelete(cred.id)"
            >
              {{ deletingId === cred.id ? 'Removing...' : 'Remove' }}
            </button>
          </div>
        </div>
        <p v-else class="mb-4 text-sm text-gpx_gray-700">No passkeys registered yet.</p>

        <div v-if="showNameInput" class="mb-4">
          <label class="mb-2 inline-block text-sm" for="passkey-name">Passkey name</label>
          <div class="flex gap-2">
            <VInput
              id="passkey-name"
              v-model="passkeyName"
              placeholder="e.g. MacBook Pro"
              class="flex-1"
            />
            <Button
              :loading="isRegistering"
              loadingText="Registering..."
              @click="handleRegister"
            >
              Save
            </Button>
            <Button
              variant="tertiary"
              @click="showNameInput = false"
            >
              Cancel
            </Button>
          </div>
        </div>
        <div v-else class="text-right">
          <Button
            variant="tertiary"
            class="w-40"
            @click="showNameInput = true"
          >
            Add passkey
          </Button>
        </div>

        <p v-if="error" class="mt-2 text-sm text-gpx_red-200">{{ error }}</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useToast } from 'vue-toastification';
import { usePasskeyAuth } from '@gpx-spa/ui';
import { getPasskeyCredentials, passkeyBeginRegister, passkeyFinishRegister, passkeyDelete } from '@gpx-spa/queries';
import VInput from '@/components/ui/VInput.vue';
import { Button } from '@gpx-spa/ui';

const toast = useToast();

const {
  isSupported: passkeySupported,
  register: passkeyRegister,
  error: passkeyBrowserError,
} = usePasskeyAuth();

const { data: credentials, isLoading: isLoadingCredentials, refetch } = getPasskeyCredentials();
const beginRegisterMutation = passkeyBeginRegister();
const finishRegisterMutation = passkeyFinishRegister();
const deleteMutation = passkeyDelete();

const showNameInput = ref(false);
const passkeyName = ref('');
const isRegistering = ref(false);
const deletingId = ref(null);

const error = computed(() => passkeyBrowserError.value);

async function handleRegister() {
  isRegistering.value = true;
  try {
    const optionsRes = await beginRegisterMutation.mutateAsync();
    const credential = await passkeyRegister(optionsRes);
    await finishRegisterMutation.mutateAsync({
      credential,
      name: passkeyName.value || 'Passkey',
    });
    toast.success('Passkey registered successfully.');
    passkeyName.value = '';
    showNameInput.value = false;
    refetch();
  } catch (err) {
    if (err?.response?.data?.message) {
      toast.error(err.response.data.message);
    }
  } finally {
    isRegistering.value = false;
  }
}

async function handleDelete(credentialId) {
  if (!window.confirm('Are you sure you want to remove this passkey?')) return;
  deletingId.value = credentialId;
  try {
    await deleteMutation.mutateAsync(credentialId);
    toast.success('Passkey removed.');
    refetch();
  } catch (err) {
    toast.error(err?.response?.data?.message || 'Failed to remove passkey.');
  } finally {
    deletingId.value = null;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
</script>
